import api from '@/services/utils/apiInstance.ts';

import type { PaginatedResponse } from '@/types/services/utils/response.type.ts';
import type { LegalDocumentTypeEnum } from '@/enums/legalDocument_enum.ts';
import type {
  CreateLegalDocumentRequest,
  LegalDocument,
  LegalDocumentResponseApi
} from '@/types/services/legalDocument/legalDocument.type.ts';

function mapLegalDocument(data: LegalDocumentResponseApi): LegalDocument {
  return {
    id: data.id,
    documentType: data.document_type,
    version: data.version,
    name: data.name,
    checksum: data.checksum,
    changeNote: data.change_note ?? null,
    authorId: data.author_id,
    author: data.author,
    isCurrent: data.is_current,
    createdAt: data.created_at
  }
}

class LegalDocumentService {
  // History, most recent first. `documentType` is optional (all types when omitted).
  async getPaginated(
    page: number,
    limit: number,
    documentType: LegalDocumentTypeEnum | null
  ): Promise<PaginatedResponse<LegalDocument>> {
    const query = new URLSearchParams()
    query.set('page', String(page))
    query.set('limit', String(limit))
    if (documentType) query.set('documentType', documentType)

    const response = await api.get(`/legal-document/bo?${query.toString()}`)
    return {
      total: response.data.total,
      page: response.data.page,
      limit: response.data.limit,
      maxPages: response.data.max_pages,
      data: response.data.data.map((doc: LegalDocumentResponseApi) => mapLegalDocument(doc))
    }
  }

  // The detail carries a `download_url` that the list doesn't.
  async getDownloadUrl(id: number): Promise<string> {
    const response = await api.get(`/legal-document/bo/${id}`)
    return response.data.download_url
  }

  // Published immediately and becomes the version in force. No edit/delete exists, on purpose.
  async create(request: CreateLegalDocumentRequest): Promise<LegalDocument> {
    const formData = new FormData()
    formData.append('documentType', request.documentType as string)
    formData.append('file', request.file as File)
    if (request.changeNote) formData.append('changeNote', request.changeNote)

    const response = await api.post('/legal-document/bo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return mapLegalDocument(response.data)
  }
}

export default new LegalDocumentService();
